import type { PollView } from "@/main/common/PollTypes";

/**
 * Reads the island's copy of the poll out of the mount container — the structural twin of
 * `parseInlinedBoardView`, and for the same reason: `discussion.jte` inlines
 * `<script type="application/json" data-poll-view>` right after the server-rendered block, and
 * `DiscussionPage.getProps` must read it BEFORE `createRoot()` discards the container's children
 * (rule 32).
 *
 * <p>`undefined` means "the server sent no poll": either the post is not a poll, or its server-side
 * load failed and the render is already noindex. Both render no block, which is why this never
 * defaults to an empty poll — an empty poll asserts something false. Shape-checked rather than
 * trusted: a payload without an `options` array is treated as absent.
 */
export function parseInlinedPollView(element: HTMLElement): PollView | undefined {
    const payloadEl = element.querySelector("script[data-poll-view]");
    if (!payloadEl) {
        return undefined;
    }
    try {
        return normalizePollView(JSON.parse(payloadEl.textContent ?? ""));
    } catch {
        return undefined;
    }
}

/**
 * The same shape check for the soft-navigation path, where the poll (once the page-data response
 * carries it) arrives as parsed JSON rather than as a script element.
 */
export function normalizePollView(candidate: unknown): PollView | undefined {
    if (!candidate || typeof candidate !== "object") {
        return undefined;
    }
    const parsed = candidate as Partial<PollView>;
    if (!Array.isArray(parsed.options)) {
        return undefined;
    }
    return parsed as PollView;
}

export function optionText(poll: PollView, optionId: number | null | undefined): string | null {
    if (optionId == null) {
        return null;
    }
    return poll.options.find((option) => option.id === optionId)?.text ?? null;
}
